/**
 * WorkflowPage Component
 *
 * Reusable page component for displaying orders filtered by workflow stages.
 * Used by Digitization, Approvals, Production, and Shipping pages.
 */

import {
  Page,
  Layout,
  Card,
  IndexTable,
  Text,
  Badge,
  TextField,
  InlineStack,
  BlockStack,
  useIndexResourceState,
  Pagination,
  Banner,
  SkeletonBodyText,
  EmptyState,
  Spinner,
  Tabs,
  Modal,
  Icon,
} from '@shopify/polaris';
import { NoteIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrders, useOrderDetail } from '../hooks';
import { STAGE_LABELS, STAGE_TONES, type WorkflowStage, type TrackedOrder } from '../types';
import CommunicationCenter from '../components/CommunicationCenter';
import { getDraftOrderNameColor } from '../lib/storeUtils';

// Helper to format relative time
function formatRelativeTime(timestamp: unknown): string {
  if (!timestamp) return '—';
  let dateMs: number;
  // Handle Firestore Timestamp objects ({ _seconds, _nanoseconds })
  if (typeof timestamp === 'object' && timestamp !== null && '_seconds' in timestamp) {
    dateMs = (timestamp as { _seconds: number })._seconds * 1000;
  } else {
    dateMs = new Date(timestamp as string).getTime();
  }
  if (isNaN(dateMs)) return '—';
  const diffMs = Date.now() - dateMs;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
}

// Loading skeleton
function OrderListSkeleton() {
  return (
    <Card padding="0">
      <div style={{ padding: '16px' }}>
        <SkeletonBodyText lines={10} />
      </div>
    </Card>
  );
}

export interface WorkflowPageProps {
  title: string;
  stages: WorkflowStage[];
  defaultStage?: WorkflowStage;
  emptyMessage?: string;
  tabLabels?: Partial<Record<WorkflowStage, string>>;
  primaryAction?: {
    content: string;
    url?: string;
    onAction?: () => void;
  };
  /** Optional filter param passed to the orders API (e.g., 'exclude_unpaid_drafts') */
  filter?: string;
}

export default function WorkflowPage({
  title,
  stages,
  defaultStage,
  emptyMessage = 'No orders found in this workflow stage',
  tabLabels,
  primaryAction,
  filter,
}: WorkflowPageProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL
  const initialStage = searchParams.get('stage') as WorkflowStage | null;
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page') || '1');

  // Validate initial stage is in our allowed stages
  const validInitialStage =
    initialStage && stages.includes(initialStage) ? initialStage : defaultStage || stages[0];

  // Local state for form inputs
  const [searchValue, setSearchValue] = useState(initialSearch);
  const [stageFilter, setStageFilter] = useState<string>(validInitialStage);
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Sort state
  const SORT_KEYS = ['shopifyOrderName', 'customerName', '', '', 'createdAt'];
  const [sortColumnIndex, setSortColumnIndex] = useState(4); // default: Created
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('descending');
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    stages.indexOf(validInitialStage)
  );

  // Quick-view communication modal
  const [quickViewOrderId, setQuickViewOrderId] = useState<string | null>(null);
  const { data: quickViewData, loading: quickViewLoading, refetch: refetchQuickView } = useOrderDetail(quickViewOrderId || undefined);

  const handleOpenQuickView = useCallback((orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickViewOrderId(orderId);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    setQuickViewOrderId(null);
  }, []);

  // Search is committed on Enter / clear; the input value alone does not trigger fetches
  const [committedSearch, setCommittedSearch] = useState(initialSearch);

  const submitSearch = useCallback(() => {
    setCommittedSearch(searchValue);
    setCurrentPage(1);
  }, [searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setCommittedSearch('');
    setCurrentPage(1);
  }, []);

  // Sort handler
  const handleSort = useCallback((headingIndex: number, direction: 'ascending' | 'descending') => {
    setSortColumnIndex(headingIndex);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const sortBy = SORT_KEYS[sortColumnIndex] || 'createdAt';
  const sortDir = sortDirection === 'ascending' ? 'asc' : 'desc';

  // Fetch orders with current filters
  const { data, loading, error, refetch, setQuery } = useOrders({
    stage: stageFilter as WorkflowStage,
    search: committedSearch || undefined,
    page: currentPage,
    limit: 20,
    filter,
    sortBy,
    sortDirection: sortDir as 'asc' | 'desc',
  });

  // Update query when filters change
  useEffect(() => {
    setQuery({
      stage: stageFilter as WorkflowStage,
      search: committedSearch || undefined,
      page: currentPage,
      limit: 20,
      filter,
      sortBy,
      sortDirection: sortDir as 'asc' | 'desc',
    });

    // Update URL params
    const params = new URLSearchParams();
    params.set('stage', stageFilter);
    if (committedSearch) params.set('search', committedSearch);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [stageFilter, committedSearch, currentPage, sortBy, sortDir, filter, setQuery, setSearchParams]);

  // Selection state
  const orders = data?.orders || [];
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(orders.map((o) => ({ id: o.id })));

  // Tab handling
  const tabs = stages.map((stage) => ({
    id: stage,
    content: tabLabels?.[stage] || STAGE_LABELS[stage],
    badge: data?.stageCounts?.[stage]?.toString() || '0',
  }));

  const handleTabChange = useCallback(
    (index: number) => {
      setSelectedTabIndex(index);
      setStageFilter(stages[index]);
      setCurrentPage(1);
    },
    [stages]
  );

  // Pagination
  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;
  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  const handleNextPage = useCallback(() => {
    if (hasNext) setCurrentPage((p) => p + 1);
  }, [hasNext]);

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious) setCurrentPage((p) => p - 1);
  }, [hasPrevious]);

  // Row markup
  const rowMarkup = orders.map((order: TrackedOrder, index: number) => {
    const lineItemCount = order.lineItems.length;
    const itemText = lineItemCount === 1 ? '1 item' : `${lineItemCount} items`;

    return (
      <IndexTable.Row
        id={order.id}
        key={order.id}
        selected={selectedResources.includes(order.id)}
        position={index}
        onClick={() => navigate(`/orders/${order.id}`)}
      >
        <IndexTable.Cell>
          <InlineStack gap="200" blockAlign="center">
            <span style={{ color: order.isDraft ? getDraftOrderNameColor(order.shopDomain) : undefined }}>
              <Text as="span" variant="bodyMd" fontWeight="bold">
                {order.shopifyOrderName}
              </Text>
            </span>
            {order.isDraft && <Badge tone="warning">Draft</Badge>}
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{order.customerName}</IndexTable.Cell>
        <IndexTable.Cell>{itemText}</IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={STAGE_TONES[order.currentStage]}>
            {STAGE_LABELS[order.currentStage]}
          </Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>{formatRelativeTime(order.createdAt)}</IndexTable.Cell>
        <IndexTable.Cell>
          <div onClick={(e) => handleOpenQuickView(order.id, e)} style={{ cursor: 'pointer', display: 'inline-flex' }}>
            <Icon source={NoteIcon} tone="subdued" />
          </div>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  // Build primary action for Page
  const pagePrimaryAction = primaryAction
    ? {
        content: primaryAction.content,
        url: primaryAction.url,
        onAction: primaryAction.onAction,
      }
    : undefined;

  return (
    <Page title={title} primaryAction={pagePrimaryAction}>
      {error && (
        <Layout.Section>
          <Banner
            title="Error loading orders"
            tone="critical"
            action={{ content: 'Retry', onAction: refetch }}
          >
            <p>{error}</p>
          </Banner>
        </Layout.Section>
      )}

      <Layout>
        {/* Stage Tabs */}
        <Layout.Section>
          <Card padding="0">
            <Tabs tabs={tabs} selected={selectedTabIndex} onSelect={handleTabChange} />
          </Card>
        </Layout.Section>

        {/* Search */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="center">
              <div
                style={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitSearch();
                  }
                }}
              >
                <TextField
                  label="Search"
                  labelHidden
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder="Search by order #, customer name, or email — press Enter"
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={clearSearch}
                />
              </div>
              {loading && <Spinner size="small" />}
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Orders Table */}
        <Layout.Section>
          {loading && orders.length === 0 ? (
            <OrderListSkeleton />
          ) : orders.length === 0 ? (
            <Card>
              <EmptyState
                heading="No orders"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  {committedSearch
                    ? 'Try adjusting your search'
                    : emptyMessage}
                </p>
              </EmptyState>
            </Card>
          ) : (
            <Card padding="0">
              <IndexTable
                resourceName={{ singular: 'order', plural: 'orders' }}
                itemCount={orders.length}
                selectedItemsCount={
                  allResourcesSelected ? 'All' : selectedResources.length
                }
                onSelectionChange={handleSelectionChange}
                sortable={[true, true, false, false, true, false]}
                sortColumnIndex={sortColumnIndex}
                sortDirection={sortDirection}
                onSort={handleSort}
                headings={[
                  { title: 'Order' },
                  { title: 'Customer' },
                  { title: 'Products' },
                  { title: 'Stage' },
                  { title: 'Created' },
                  { title: '' },
                ]}
              >
                {rowMarkup}
              </IndexTable>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                  <InlineStack align="center" gap="400">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Showing {(currentPage - 1) * (data?.limit || 20) + 1}-
                      {Math.min(currentPage * (data?.limit || 20), data?.total || 0)} of{' '}
                      {data?.total || 0}
                    </Text>
                    <Pagination
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onNext={handleNextPage}
                      onPrevious={handlePreviousPage}
                    />
                  </InlineStack>
                </div>
              )}
            </Card>
          )}
        </Layout.Section>
      </Layout>

      {/* Quick View Communication Modal */}
      <Modal
        open={quickViewOrderId !== null}
        onClose={handleCloseQuickView}
        title={quickViewData?.order ? `Communications — ${quickViewData.order.shopifyOrderName} (${quickViewData.order.customerName})` : 'Loading...'}
        size="large"
      >
        <Modal.Section>
          {quickViewLoading ? (
            <BlockStack gap="400">
              <SkeletonBodyText lines={6} />
            </BlockStack>
          ) : quickViewData?.order ? (
            <CommunicationCenter
              order={quickViewData.order}
              approvals={quickViewData.approvals}
              inboundEmails={quickViewData.inboundEmails}
              emailLogs={quickViewData.emailLogs}
              gmailMessages={quickViewData.gmailMessages}
              onEmailSent={refetchQuickView}
            />
          ) : (
            <Text as="p" tone="subdued">Unable to load communications</Text>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
